import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	FormControl,
	MenuItem,
	OutlinedInput,
	Select,
	Stack,
	TablePagination,
	Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { uniq } from 'lodash';
import * as React from 'react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuthContext } from '../../../../../context/auth';
import { useConfigContext } from '../../../../../context/config';
import { dayjs } from '../../../../../date-util';
import ResourcesAPI from '../../api/resources';
import {
	DATE_COMPLIANCE_ADD_BUTTON_TEXT,
	DATE_COMPLIANCE_HEADER,
	DATE_COMPLIANCE_ROW_DATE_FORMAT,
	DATE_COMPLIANCE_SUBHEADER,
	DATE_COMPLIANCE_SUBHEADER_2,
	DATE_COMPLIANCE_TIMESTAMPS_LIST_HEADER,
	DATE_COMPLIANCE_TIMESTAMPS_PRODUCT_LABEL,
} from '../../data/constants';
import DeployResourcesModal from './components/DeployResourcesModal';
import ResourcesList from './components/ResourcesList';

export const RESOURCES_KEY = 'resources';

export type Order = 'asc' | 'desc';

export default function ResourceGroups() {
	const { tenantHostname } = useParams();
	const authData = useAuthContext();
	const authToken = authData?.tenantAuthorizationData?.authorizationToken;
	const tenantId = authData?.tenantAuthorizationData?.tenantId;

	const config = useConfigContext();
	const productConfigs = config?.productConfigs;
	const products = productConfigs
		? uniq(
				productConfigs
					.filter((config) => config.name !== 'global')
					.map((product) => {
						return {
							name: product.name,
							value: product.name,
						};
					}),
		  )
		: [];
	const [product, setProduct] = useState<string>(
		products[0] ? products[0].name : '',
	);
	const [modalOpen, setModalOpen] = useState<boolean>(false);
	const [expandedItem, setExpandedItem] = useState<number | false>(false);
	const [page, setPage] = useState<number>(0);
	const [rowsPerPage, setRowsPerPage] = useState<number>(10);
	const [order, setOrder] = useState<Order>('desc');

	const { data, isLoading } = ResourcesAPI.useResourcesTimestamps(
		[RESOURCES_KEY, tenantId || tenantHostname, product],
		product,
		authToken,
		tenantHostname,
	);

	const timestampListToPresent = data
		?.sort((a, b) => {
			const direction = order === 'asc' ? 1 : -1;
			if (a < b) {
				return -1 * direction;
			}
			if (a > b) {
				return 1 * direction;
			}
			return 0;
		})
		.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

	const handleAccordionChange =
		(effectiveTimestamp: number) =>
		(_event: React.SyntheticEvent, isExpanded: boolean) => {
			setExpandedItem(isExpanded ? effectiveTimestamp : false);
		};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleSort = (event: React.MouseEvent<unknown>) => {
		const isAsc = order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
	};

	return (
		<Stack
			spacing={2}
			sx={{
				flex: 1,
				overflowY: 'auto',
				height: '100%',
			}}
		>
			<Stack direction='column' spacing={2}>
				<Typography variant='h3'>{DATE_COMPLIANCE_HEADER()}</Typography>
				<Stack>
					<Typography variant='subtitle1'>
						{DATE_COMPLIANCE_SUBHEADER()}
					</Typography>
					<Typography variant='subtitle1'>
						{DATE_COMPLIANCE_SUBHEADER_2()}
					</Typography>
				</Stack>
			</Stack>
			<Stack border={1} p={3} borderColor={grey[200]}>
				<Box display='flex' alignItems='center' justifyContent='space-between'>
					<FormControl>
						<Typography fontWeight={500} variant='h4' mb={1}>
							{DATE_COMPLIANCE_TIMESTAMPS_PRODUCT_LABEL()}
						</Typography>
						<Select
							multiple={false}
							value={product}
							size='small'
							onChange={(e) => setProduct(e.target.value)}
							sx={{ width: '200px' }}
							input={<OutlinedInput />}
						>
							{products.map((p) => (
								<MenuItem key={p.name} value={p.name}>
									{p.value}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<Button
						variant='contained'
						color='secondary'
						startIcon={<AddIcon />}
						onClick={() => setModalOpen(true)}
					>
						{DATE_COMPLIANCE_ADD_BUTTON_TEXT()}
					</Button>
				</Box>

				<Stack direction='column' mt={3} spacing={2}>
					<Stack direction='row' spacing={2} justifyContent='space-between'>
						<Stack
							onClick={handleSort}
							direction='row'
							spacing={1}
							alignContent='center'
							mt={2}
						>
							<Typography variant='body1' align='left'>
								{DATE_COMPLIANCE_TIMESTAMPS_LIST_HEADER()}
							</Typography>
							{order === 'asc' ? (
								<ArrowUpward sx={{ color: grey[600] }} />
							) : (
								<ArrowDownward sx={{ color: grey[600] }} />
							)}
						</Stack>
					</Stack>
					{timestampListToPresent?.map((effectiveTimestamp) => (
						<Box
							key={effectiveTimestamp}
							sx={{
								direction: 'row',
								justifyContent: 'space-between',
								minWidth: '1000px',
							}}
						>
							<Accordion
								expanded={expandedItem === effectiveTimestamp}
								onChange={handleAccordionChange(effectiveTimestamp)}
							>
								<AccordionSummary
									sx={{
										backgroundColor:
											expandedItem !== effectiveTimestamp
												? 'white.grey'
												: grey[100],
									}}
								>
									<Stack
										direction='row'
										spacing={2}
										alignContent='center'
										p={1}
									>
										{expandedItem !== effectiveTimestamp ? (
											<AddCircleOutlineRoundedIcon color={'secondary'} />
										) : (
											<RemoveCircleOutlineRoundedIcon color={'secondary'} />
										)}
										<Typography fontWeight={500} variant='body1'>
											{dayjs(effectiveTimestamp).format(
												DATE_COMPLIANCE_ROW_DATE_FORMAT(),
											)}
										</Typography>
									</Stack>
								</AccordionSummary>
								<AccordionDetails>
									{expandedItem === effectiveTimestamp && (
										<ResourcesList
											product={product}
											effectiveTimestamp={effectiveTimestamp}
										/>
									)}
								</AccordionDetails>
							</Accordion>
						</Box>
					))}

					<Stack direction='row' justifyContent='flex-end'>
						{data && !isLoading && data.length > 0 && (
							<TablePagination
								rowsPerPageOptions={[5, 10, 25]}
								component='div'
								count={data?.length}
								rowsPerPage={rowsPerPage}
								page={page}
								onPageChange={(e, page) => setPage(page)}
								onRowsPerPageChange={handleChangeRowsPerPage}
							/>
						)}
					</Stack>
				</Stack>
			</Stack>
			<DeployResourcesModal
				handleClose={() => setModalOpen(false)}
				open={modalOpen}
				isProductEditable={true}
				product={product}
			/>
		</Stack>
	);
}
